import { useEffect, useMemo, useState } from "react";
import { Button, message, Space } from "antd";
import PvTable from "../../components/Table/PvTable";
import ConfirmDelete from "../../components/ConfirmDelete/ConfirmDelete";
import Popup from "../../components/Popup";
import PvForm from "../../components/Form/PvForm";
import useSpectatorAccessService from "../../services/PositionAdmin/spectatorAccessService";
import usePositionService from "../../services/PositionAdmin/positionService";
import useAxios from "../../hooks/useAxios";
import { normalizeEmployeeStatus } from "../../utils/generalUtils";

const SpectatorAccess = () => {
  const {
    getAllSpectatorAccesses,
    listLoading,
    listResponse,
    addSpectatorAccess,
    removeSpectatorAccess,
  } = useSpectatorAccessService();

  const { getAllApprovers, approverLoading, approverResponse } =
    usePositionService();

  const axiosEmployees = useAxios();

  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [approverOptions, setApproverOptions] = useState([]);

  useEffect(() => {
    getAllSpectatorAccesses();
    getAllApprovers();
    axiosEmployees.fetchData({ url: `/admin/employee`, method: "GET" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!listResponse?.data) return;

    const mapped = listResponse.data.map((item, index) => ({
      key: index + 1,
      id: item.id,
      employee_name: item.employee
        ? [item.employee.first_name, item.employee.middle_name, item.employee.last_name]
            .filter(Boolean)
            .join(" ")
        : `Employee ${item.employee_id}`,
      approver_position: item.approver_position
        ? `${item.approver_position.position_name} - ${item.approver_position.unit}`
        : `Position ${item.approver_position_id}`,
    }));
    setTableData(mapped);
  }, [listResponse]);

  useEffect(() => {
    if (!approverResponse?.data) return;

    setApproverOptions(
      approverResponse.data.map((pos) => ({
        name: pos.id,
        label: `${pos.position_name} - ${pos.unit}`,
      }))
    );
  }, [approverResponse]);

  useEffect(() => {
    if (!axiosEmployees.response?.data) return;

    setEmployeeOptions(
      axiosEmployees.response.data
        .filter((emp) => {
          if (!emp.first_name || !emp.last_name) return false;
          const SUFFIX_RE = /\s+(Jr\.?|Sr\.?|XII|XIII|VIII|VII|VI|IV|III|II|XI|IX|X|V|I)$/i;
          const fullName = [emp.first_name, emp.middle_name, emp.last_name].filter(Boolean).join(" ");
          if (/\d/.test(fullName.replace(SUFFIX_RE, ""))) return false;
          if (normalizeEmployeeStatus(emp).is_active === false) return false;
          return true;
        })
        .map((emp) => ({
          name: emp.id,
          label: [emp.first_name, emp.middle_name, emp.last_name]
            .filter(Boolean)
            .join(" "),
          last_name: emp.last_name ?? "",
        }))
        .sort((a, b) => a.last_name.localeCompare(b.last_name))
    );
  }, [axiosEmployees.response]);

  const handleRemove = async (id) => {
    try {
      await removeSpectatorAccess(id);
      message.success("Spectator access removed.");
      getAllSpectatorAccesses();
    } catch {
      message.error("Failed to remove spectator access.");
    }
  };

  const columns = [
    { title: "No.", dataIndex: "key", rowScope: "row", width: 60 },
    { title: "Employee", dataIndex: "employee_name", key: "employee_name" },
    {
      title: "Watches Approver Position",
      dataIndex: "approver_position",
      key: "approver_position",
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => (
        <ConfirmDelete
          isService={true}
          buttonText="Remove"
          onDeleted={() => handleRemove(item.id)}
        />
      ),
    },
  ];

  const formFields = useMemo(
    () => [
      {
        name: "employee_id",
        label: "Employee",
        type: "select",
        selectItems: employeeOptions,
        searchable: true,
        rules: [{ required: true, message: "Please select an employee." }],
      },
      {
        name: "approver_position_id",
        label: "Approver Position to Watch",
        type: "select",
        selectItems: approverOptions,
        rules: [{ required: true, message: "Please select an approver position." }],
      },
    ],
    [employeeOptions, approverOptions]
  );

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button
          size="large"
          type="primary"
          style={{ background: "#00573F" }}
          onClick={() => setIsModalOpen(true)}
        >
          Add AO Access
        </Button>
      </Space>

      <PvTable
        data={tableData}
        columns={columns}
        loading={listLoading || approverLoading}
        onRowClick={() => {}}
      />

      <Popup
        title="Add AO Access"
        isOpen={isModalOpen}
        footer={null}
        onCancel={() => setIsModalOpen(false)}
      >
        <PvForm
          isService={true}
          formFields={formFields}
          onSubmitted={async (values) => {
            try {
              await addSpectatorAccess(
                values.employee_id,
                values.approver_position_id
              );
              message.success("Spectator access added.");
              setIsModalOpen(false);
              getAllSpectatorAccesses();
            } catch {
              message.error("Failed to add spectator access.");
            }
          }}
        />
      </Popup>
    </>
  );
};

export default SpectatorAccess;
